/**
 * Post-login redirect back to CRM Passport `/oauth/authorize` (e.g. PBv2 PKCE flow).
 * Redirect host must match the configured API host (VITE_API_URL) and use an api.* hostname.
 */

const API_HOST_PREFIX = /^api\./i;

/**
 * Hostname from VITE_API_URL (the CRM API this Portal build targets).
 */
export function getExpectedApiHostname(): string {
  const raw = import.meta.env.VITE_API_URL?.trim() ?? '';
  if (!raw) {
    return '';
  }
  try {
    return new URL(raw).hostname;
  } catch {
    return '';
  }
}

/**
 * True if `redirectUrl` points to this stack's CRM OAuth authorize endpoint on the expected API host.
 */
export function isTrustedOAuthAuthorizeReturnUrl(
  redirectUrl: string,
  expectedApiHostname: string = getExpectedApiHostname()
): boolean {
  if (!redirectUrl || !expectedApiHostname) return false;

  try {
    const u = new URL(redirectUrl);
    if (u.protocol !== 'https:' && u.protocol !== 'http:') {
      return false;
    }
    if (u.pathname !== '/oauth/authorize') {
      return false;
    }
    if (!API_HOST_PREFIX.test(u.hostname)) {
      return false;
    }
    if (u.hostname !== expectedApiHostname) {
      return false;
    }

    return true;
  } catch {
    return false;
  }
}

export function readOAuthAuthorizeRedirectFromSearch(): string | null {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get('redirect');
}

const AUTH_LOOP_PATHS = new Set(['/', '/login', '/logout']);

/**
 * True if `path` is a safe same-portal relative return path (pathname + search only).
 */
export function isTrustedPortalReturnPath(path: string): boolean {
  if (!path || !path.startsWith('/') || path.startsWith('//')) {
    return false;
  }
  if (path.includes('://') || path.includes('\\')) {
    return false;
  }
  if (path.includes('#')) {
    return false;
  }

  const pathname = path.split('?')[0] ?? path;
  if (AUTH_LOOP_PATHS.has(pathname)) {
    return false;
  }

  return true;
}

export type PostLoginRedirect =
  | { kind: 'oauth'; target: string }
  | { kind: 'portal'; target: string };

export function readPostLoginRedirectFromSearch(
  search: string = typeof window !== 'undefined' ? window.location.search : ''
): PostLoginRedirect | null {
  const raw = new URLSearchParams(search).get('redirect');
  if (!raw) {
    return null;
  }

  if (isTrustedOAuthAuthorizeReturnUrl(raw)) {
    return { kind: 'oauth', target: raw };
  }

  if (isTrustedPortalReturnPath(raw)) {
    return { kind: 'portal', target: raw };
  }

  return null;
}

export function buildLoginPathWithRedirect(
  returnPath: string,
  extraParams: Record<string, string> = {}
): string {
  const params = new URLSearchParams();

  Object.entries(extraParams).forEach(([key, value]) => {
    if (value) {
      params.set(key, value);
    }
  });

  if (isTrustedPortalReturnPath(returnPath)) {
    params.set('redirect', returnPath);
  }

  const query = params.toString();
  return query ? `/login?${query}` : '/login';
}
